import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Email,
  Business,
  AdminPanelSettings,
  Edit,
  Save,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const UserProfile: React.FC = () => {
  const { user, profile, organization, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleEdit = () => {
    setDisplayName(profile?.display_name || '');
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setDisplayName(profile?.display_name || '');
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('表示名を入力してください');
      return;
    }

    if (displayName.length < 2) {
      setError('表示名は2文字以上で入力してください');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await updateProfile({ display_name: displayName.trim() });
      setSuccess('プロフィールを更新しました');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'プロフィールの更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin':
        return '管理者';
      case 'member':
        return 'メンバー';
      default:
        return role;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        ユーザープロフィール
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* プロフィール情報 */}
        <Paper sx={{ p: 3, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                bgcolor: 'primary.main',
                fontSize: '2rem',
              }}
            >
              {profile?.display_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" gutterBottom>
                基本情報
              </Typography>
              <Typography variant="body2" color="text.secondary">
                アカウントの基本情報を管理します
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Person sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle1" fontWeight="bold">
                表示名
              </Typography>
            </Box>
            {isEditing ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="表示名を入力"
                  size="small"
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : '保存'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  キャンセル
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  {profile?.display_name || '未設定'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Edit />}
                  onClick={handleEdit}
                >
                  編集
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Email sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle1" fontWeight="bold">
                メールアドレス
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              {user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              メールアドレスの変更はサポートまでお問い合わせください
            </Typography>
          </Box>
        </Paper>

        {/* 所属組織情報 */}
        <Paper sx={{ p: 3, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Business sx={{ mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" gutterBottom>
                所属組織
              </Typography>
              <Typography variant="body2" color="text.secondary">
                参加しているマンション管理組合の情報
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {organization ? (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Typography variant="h6" component="h3">
                    {organization.name}
                  </Typography>
                  <Chip
                    icon={<AdminPanelSettings />}
                    label={getRoleDisplayName(profile?.role || '')}
                    color={profile?.role === 'admin' ? 'primary' : 'default'}
                    size="small"
                  />
                </Box>
                
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Business fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="組織名"
                      secondary={organization.name}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AdminPanelSettings fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="あなたの権限"
                      secondary={getRoleDisplayName(profile?.role || '')}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="warning" sx={{ mb: 3 }}>
              所属している組織がありません
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            複数の組織への参加や組織の切り替えは今後のアップデートで対応予定です。
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default UserProfile;